<template>
  <div
    id="organization"
    v-if="organization"
    class="mt-3">
    <!-- Organization Form -->
    <form
      class="container"
      @submit.prevent="submitOrganization(organization)">

      <!-- Name / Timezone -->
      <div class="row mt-3">
        <!-- Name -->
        <div class="col-sm">
          <label for="organization-name">
            Organization:
            <small class="text-danger">required</small>
          </label>
          <b-form-input
            id="organization-name"
            ref="organizationName"
            name="organizationName"
            type="text"
            placeholder="Enter an organization name"
            v-model="organization.name"
            :readonly="submitting">
          </b-form-input>
        </div>
        <!-- / Name -->

        <!-- Timezone -->
        <div class="col-sm mt-3 mt-sm-0">
          <label for="organization-timezone">Timezone: <small class="text-danger">required</small></label>
          <b-form-select
            id="organization-timezone"
            ref="organizationTimezone"
            name="organizationTimezone"
            v-model="organization.timezone"
            :disabled="submitting">
            <option selected :value="undefined">Select Timezone</option>
            <option
              v-for="(value, key) in timezones"
              :key="key"
              :value="key">
              {{value}}
            </option>
          </b-form-select>
        </div>
        <!-- / Timezone -->
      </div>
      <!-- / Name / Timezone -->

      <!-- Email / Phone -->
      <div class="row mt-3">
        <!-- Email -->
        <div class="col-sm">
          <label for="organization-email">Email: <small class="text-danger">required</small></label>
          <b-input-group>
            <b-form-input
              id="organization-email"
              ref="organizationEmail"
              name="organizationEmail"
              type="email"
              placeholder="Enter an email address"
              v-model="organization.email"
              :readonly="submitting">
            </b-form-input>
            <b-input-group-append is-text>
              <b-form-checkbox
                v-b-tooltip.hover.right
                :title="`Check this box if you want
                registration order confirmation emails bcc'd to ${organizationCopy.email}`"
                v-model="organization.bcc_order_emails">
                <small>BCC Conf. Emails</small>
              </b-form-checkbox>
            </b-input-group-append>
          </b-input-group>
        </div>
        <!-- / Email -->

        <!-- Phone -->
        <div class="col-sm mt-3 mt-sm-0">
          <label for="organization-phone">Phone: <small class="text-danger">required</small></label>
          <b-form-input
            id="organization-phone"
            ref="organizationPhone"
            name="organizationPhone"
            type="tel"
            placeholder="Enter a phone number"
            v-model="organization.phone"
            :readonly="submitting">
          </b-form-input>
        </div>
        <!-- / Phone -->
      </div>
      <!-- / Email / Phone -->

      <!-- Contact Name / URL -->
      <div class="row mt-3">
        <!-- Contact Name -->
        <div class="col-sm">
          <label for="organization-contact-name">Contact Name: <small class="text-danger">required</small></label>
          <b-form-input
            id="organization-contact-name"
            ref="organizationContactName"
            name="organizationContactName"
            type="text"
            placeholder="Enter a contact name"
            v-model="organization.contact_name"
            :readonly="submitting">
          </b-form-input>
        </div>
        <!-- / Contact Name -->

        <!-- URL -->
        <div class="col-sm mt-3 mt-sm-0">
          <label for="organization-url">Website: <small class="text-danger">required</small></label>
          <b-form-input
            id="organization-url"
            ref="organizationURL"
            name="organizationURL"
            type="url"
            placeholder="Enter a website URL"
            v-model="organization.url"
            :readonly="submitting">
          </b-form-input>
        </div>
        <!-- / URL -->
      </div>
      <!-- / Contact Name / URL -->

      <!-- Address Line 1 & 2 -->
      <div class="row mt-3">
        <!-- Address Line 1 -->
        <div class="col-sm">
          <label for="organization-addr-1">Address Line 1: <small class="text-danger">required</small></label>
          <b-form-input
            id="organization-addr-1"
            ref="organizationAddr1"
            name="organizationAddr1"
            type="text"
            placeholder="Enter an address"
            v-model="organization.address_line1"
            :readonly="submitting">
          </b-form-input>
        </div>
        <!-- / Address Line 1 -->

        <!-- Address Line 2 -->
        <div class="col-sm mt-3 mt-sm-0">
          <label for="organization-addr-2">Address Line 2:</label>
          <b-form-input
            id="organization-addr-2"
            ref="organizationAddr2"
            name="organizationAddr2"
            type="text"
            placeholder="Enter an address"
            v-model="organization.address_line2"
            :readonly="submitting">
          </b-form-input>
        </div>
        <!-- / Address Line 2 -->
      </div>
      <!-- / Address Line 1 & 2 -->

      <!-- City, State, & ZIP Code -->
      <div class="row mt-3">
        <!-- City -->
        <div class="col-sm-6">
          <label for="organization-addr-city">City: <small class="text-danger">required</small></label>
          <b-form-input
            id="organization-addr-city"
            ref="organizationAddrCity"
            name="organizationAddrCity"
            type="text"
            placeholder="Enter a city"
            v-model="organization.address_city"
            :readonly="submitting">
          </b-form-input>
        </div>
        <!-- / City -->

        <!-- State -->
        <div class="col-sm-3 mt-3 mt-sm-0">
          <label for="organization-addr-state">State: <small class="text-danger">required</small></label>
          <b-form-select
            id="organization-addr-state"
            ref="organizationAddrState"
            name="organizationAddrState"
            v-model="organization.address_state"
            :readonly="submitting">
            <option selected :value="null">Select State</option>
            <option
              v-for="(value, key) in states"
              :key="key"
              :value="key">
              {{value}}
            </option>
          </b-form-select>
        </div>
        <!-- / State -->

        <!-- ZIP Code -->
        <div class="col-sm-3 mt-3 mt-sm-0">
          <label for="organization-addr-zipcode">ZIP Code: <small class="text-danger">required</small></label>
          <b-form-input
            id="organization-addr-zipcode"
            ref="organizationAddrZIPCode"
            name="organizationAddrZIPCode"
            type="text"
            placeholder="Enter a ZIP code"
            v-model="organization.address_postal_code"
            :readonly="submitting">
          </b-form-input>
        </div>
        <!-- / ZIP Code -->
      </div>
      <!-- City, State, & ZIP Code -->

      <!-- Statement Descriptor -->
      <div class="row mt-3">
        <!-- Statement Descriptor -->
        <div class="col-sm-6">
          <label for="organization-statement-descriptor">
            Statement Descriptor:
            <small class="text-danger">Required. Enter a max of 10 characters.</small>
          </label>
          <b-form-input
            id="organization-statement-descriptor"
            ref="organizationStatementDescriptor"
            name="organizationStatementDescriptor"
            type="text"
            maxlength="10"
            placeholder="Enter a string that will appear on credit card statements"
            v-model="statementDescriptor"
            :readonly="submitting">
          </b-form-input>
          <small class="d-block mt-2">
            Will appear on credit card statements as:<br>
            <span class="text-uppercase">KJKREG* {{statementDescriptor}}</span>
          </small>
        </div>
        <!-- / Statement Descriptor -->
      </div>
      <!-- / Statement Descriptor -->

      <div class="corner-buttons">
        <!-- Save Organization Button -->
        <b-button
          class="shadow"
          type="submit"
          variant="success"
          :disabled="submitting">
          <font-awesome-icon icon="save"></font-awesome-icon> Save
        </b-button>
        <!-- / Save Organization Button -->

        <!-- Delete Organization Button -->
        <b-button
          class="shadow ml-3"
          type="button"
          variant="danger"
          v-if="organization.id"
          :disabled="submitting"
          @click="submitOrganization(organization.id)">
          <font-awesome-icon icon="trash"></font-awesome-icon> Delete Organization
        </b-button>
        <!-- / Delete Organization Button -->
      </div>
    </form>
    <!-- / Organization Form -->
  </div>
</template>

<script>
import _ from 'lodash';
import DataSvc from '../services/data';
import EventBus from '../services/event-bus';
import DirtyForm from '../services/dirty-form';
import AppMixin from '../services/app-mixin';

export default {
  name: 'organization',
  mixins: [
    DirtyForm,
    AppMixin,
  ],
  data() {
    return {
      organization: false,
      organizationCopy: false,
      submitting: false,
    };
  },
  computed: {
    statementDescriptor: {
      get() {
        return this.organization.statement_descriptor.toUpperCase();
      },
      set(str = '') {
        this.organization.statement_descriptor = str.toUpperCase();
      },
    },
    organizationDirty() {
      const organizationJSON = JSON.stringify(this.organization);
      const organizationCopyJSON = JSON.stringify(this.organizationCopy);
      return organizationJSON !== organizationCopyJSON;
    },
    anyDirty() {
      return this.organizationDirty;
    },
  },
  methods: {
    submitOrganization(organization) {
      let orgPromise;

      this.submitting = true;

      if (typeof organization !== 'object') {
        orgPromise = this.deleteOrganization(organization);
      } else if (organization.id) {
        orgPromise = this.updateOrganization(organization);
      } else {
        orgPromise = this.createOrganization(organization);
      }

      orgPromise
        .then(() => {
          this.$router.push({ name: 'organizations' });
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.submitting = false;
          this.hideToast();
        });

      return orgPromise;
    },
    createOrganization(organization) {
      this.showToast('Creating organization...');

      return DataSvc.createOrganization(organization)
        .then(o => this.delay(1000, o))
        .then((o) => {
          this.organizations = this.organizations.concat([_.cloneDeep(o)]);
          this.organization = o;
          this.organizationCopy = _.cloneDeep(o);
          this.showToast('Organization created!');
        });
    },
    updateOrganization(organization) {
      this.showToast('Updating organization...');

      return DataSvc.updateOrganization(organization)
        .then((o) => {
          const org = _.find(this.organizations, { id: o.id });
          _.extend(org, o);
          this.organization = o;
          this.organizationCopy = _.cloneDeep(o);
          return this.delay(1000);
        })
        .then(() => {
          this.showToast('Organization updated!');
        });
    },
    deleteOrganization(organizationID) {
      const confirmDelete = () => {
        this.showToast('Deleting organization...');

        return DataSvc.deleteOrganization(organizationID)
          .then(() => {
            // remove from existing organizations
            this.organizations = _.reject(this.organizations, { id: organizationID });
            return this.delay(1000);
          })
          .then(() => {
            this.showToast('Organization deleted!');

            // if acting organization was deleted
            if (this.loggedInUser.actingOrg.id === organizationID) {
              EventBus.$emit('set-acting-org', this.organizations[0]);
            }
          });
      };

      return this.showConfirm(
        'Are you sure you want to delete this organization?',
        'Deleting an organization',
      ).then(confirmDelete);
    },
  },
  created() {
    this.showGlobalLoader('Loading organization...');

    const initOrg = () => {
      const { organizationID } = this.$route.params;
      let orgPromise;

      if (organizationID === 'new') {
        orgPromise = Promise.resolve({ statement_descriptor: '' });
      } else {
        orgPromise = DataSvc.getOrganization(organizationID);
      }

      return orgPromise
        .catch(() => {
          this.showWarning('That organization does not exist.');
          this.$router.replace({ name: 'organizations' });
        })
        .then((organization) => {
          this.organization = organization;
          this.organizationCopy = _.cloneDeep(organization);

          // focus first input
          this.$nextTick(() => {
            this.focusInput('organizationName');
          });
        });
    };

    this.initPromise.then(initOrg).finally(this.hideGlobalLoader);
  },
};
</script>
